// MST
import { types, flow, cast, detach } from 'mobx-state-tree';

// Interfaces
import {
  IAssessmentRelevantProofGetListAttrs,
  IAssessmentRelevantProofDownloadDocumentAttrs,
} from 'interfaces/back-office/assessment/registration/relevant-proof/relevant-proof-view.interface';
import {
  IAssessmentRelevantProofApiListResponse,
  IAssessmentRelevantProofApiDownloadDocumentResponse,
} from 'interfaces/back-office/assessment/registration/relevant-proof/relevant-proof-api.interface';
import { ISharedCommonLoadingAttrs, ISharedCommonLoadingQuestionSetAttrs } from 'interfaces/shared/shared-common.interface';

// Extensions
import { withEnvironment } from 'models/extensions/with-environment';

// Utils
import { withHelper_fileDownload } from 'models/extensions/with-helper';
import {
  withHelper_questionSetModel,
  withHelper_commonLoadingModel,
  withHelper_commonMapLoading,
  withHelper_commonMapQuestionSet,
  withHelper_commonMapQuestionSetLoading,
} from 'models/extensions/helper';

// Constant
import { LOADING, LOADING_KEY_TYPE } from 'constant/shared.constant';

// Types
const { optional, model, array } = types;

// Loading
const { BASE, DOWNLOAD } = LOADING;
const { ANSWER_FILES } = LOADING_KEY_TYPE;

// Base Model
const AssessmentRelevantProofStoreModel = model('AssessmentRelevantProof')
  .props({
    assessmentRelevantProof_loading: withHelper_commonLoadingModel,
    assessmentRelevantProof_list: optional(array(withHelper_questionSetModel), []),
  })
  .actions(self => ({
    assessmentRelevantProof_SET_LOADING: (payload: ISharedCommonLoadingAttrs): void => {
      const map = withHelper_commonMapLoading(self.assessmentRelevantProof_loading, payload);

      detach(self.assessmentRelevantProof_loading);
      self.assessmentRelevantProof_loading = cast(map);
    },
    assessmentRelevantProof_SET_LIST: (payload: IAssessmentRelevantProofApiListResponse['res']['data']): void => {
      const map = withHelper_commonMapQuestionSet(payload);
      // detach(self.assessmentRelevantProof_list);
      self.assessmentRelevantProof_list = cast(map);
    },
    assessmentRelevantProof_MAP_LOADING_LIST: (payload: ISharedCommonLoadingQuestionSetAttrs): void => {
      const map = withHelper_commonMapQuestionSetLoading(self.assessmentRelevantProof_list, payload);

      detach(self.assessmentRelevantProof_list);
      self.assessmentRelevantProof_list = cast(map);
    },
    assessmentRelevantProof_CLEAR_LIST: (): void => {
      self.assessmentRelevantProof_list = cast([]);
    },
  }))
  .extend(withEnvironment)
  .actions(self => ({
    /**
     * @description Fetch assessment general proof list
     *
     * @param {IAssessmentRelevantProofGetListAttrs} payload
     *
     * @return {any} any
     */
    assessmentRelevantProof_fetchList: flow(function* (payload: IAssessmentRelevantProofGetListAttrs) {
      self.assessmentRelevantProof_SET_LOADING({ type: BASE, value: true });

      const response: IAssessmentRelevantProofApiListResponse =
        yield self.environment.assessmentRelevantProofApi.assessmentRelevantProof_getList({
          params: { ...payload.params },
          requestConfig: { ...payload.requestConfig },
        });

      if (response.ok) {
        self.assessmentRelevantProof_SET_LIST(response.res.data);

        if (payload?.onSuccess) payload.onSuccess();
      }

      if (!response.ok) {
        if (payload?.onError) payload.onError();
      }

      self.assessmentRelevantProof_SET_LOADING({ type: BASE, value: false });
    }),

    /**
     * @description Download assessment general proof document
     *
     * @param {IAssessmentRelevantProofDownloadDocumentAttrs} payload
     *
     * @return {any} any
     */
    assessmentRelevantProof_downloadDocument: flow(function* (payload: IAssessmentRelevantProofDownloadDocumentAttrs) {
      const { blobId, questionSetId, resultId, resultDetailId, fileId } = payload.params;

      // Start Loading
      self.assessmentRelevantProof_MAP_LOADING_LIST({
        questionSetId,
        resultId,
        resultDetailId,
        fileId,
        type: ANSWER_FILES,
        loadingType: DOWNLOAD,
        value: true,
      });

      const response: IAssessmentRelevantProofApiDownloadDocumentResponse =
        yield self.environment.assessmentRelevantProofApi.assessmentRelevantProof_downloadDocument({
          params: { blobId },
          requestConfig: { ...payload.requestConfig },
        });

      if (response.ok) {
        withHelper_fileDownload(response.res, response.headers!);

        if (payload?.onSuccess) payload.onSuccess();
      }

      if (!response.ok) {
        if (payload?.onError) payload.onError();
      }

      // Stop Loading
      self.assessmentRelevantProof_MAP_LOADING_LIST({
        questionSetId,
        resultId,
        resultDetailId,
        fileId,
        type: ANSWER_FILES,
        loadingType: DOWNLOAD,
        value: false,
      });
    }),
  }));

export const createAssessmentRelevantProofStoreDefaultModel = () => optional(AssessmentRelevantProofStoreModel, {});
